import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as classes from './Header.module.css';
import MenuBurger from '../menu-burger/MenuBurger';
import CartButton from '../cart-button/CartButton';
import SideMenu from '../side-menu/SideMenu.js';
import CartDrawer from '../cart-drawer/CartDrawer.js';
import { CSSTransition } from 'react-transition-group';
import useBreakpoint from '../../hooks/useBreakpoint';
import { isMobile } from 'react-device-detect';
import { Link } from 'gatsby';
import getLocalizedName from '../../utils/getLocalizedName';
import { useCartDrawer, useCheckout } from '../../context/shopContext';
import MenuLayout from '../menu-layout/MenuLayout';

const Header = ({
  navigation,
  currentPage,
  translations,
  locale,
  finnishLanguage,
  backTo,
  burgerMenuOpen,
  setBurgerMenuOpen,
  topTitle
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isSmallDevice, setIsSmallDevice] = useState(false);

  const { cartDrawerOpen, setCartDrawerOpen } = useCartDrawer();

  const breakpoint = useBreakpoint();
  const checkout = useCheckout();

  useEffect(() => {
    if (isMobile) {
      setIsSmallDevice(true);
    } else {
      if (
        (breakpoint === 'mobile' || breakpoint === 'tablet') &&
        !isSmallDevice
      ) {
        setIsSmallDevice(true);
      } else if (
        isSmallDevice &&
        breakpoint !== 'mobile' &&
        breakpoint !== 'tablet'
      ) {
        setIsSmallDevice(false);
      }
    }
  }, [isSmallDevice, breakpoint]);

  return isSmallDevice ? (
    <header className={classes.smallHeader}>
      <div className={classes.smallBack}>
        <div className={classes.smallBox}>{navigation[0].title}</div>
        <div className={classes.buttonSection}>
          <div className={classes.smallBox}>
            <CartButton
              mobile
              cartDrawerOpen={cartDrawerOpen}
              setCartDrawerOpen={setCartDrawerOpen}
              burgerMenuOpen={burgerMenuOpen}
              setBurgerMenuOpen={setBurgerMenuOpen}
            />
          </div>
          <div className={classes.smallBox}>
            <MenuBurger
              burgerMenuOpen={burgerMenuOpen}
              setBurgerMenuOpen={setBurgerMenuOpen}
            />
          </div>
        </div>
      </div>
      <CSSTransition
        in={burgerMenuOpen}
        timeout={250}
        classNames={{
          enter: classes.sidemenuEnter,
          enterActive: classes.sidemenuEnterActive,
          enterDone: classes.sidemenuEnterDone,
          exit: classes.sidemenuExit,
          exitActive: classes.sidemenuExitActive,
        }}
        unmountOnExit
      >
        <SideMenu
          items={navigation}
          translations={translations}
          locale={locale}
          currentPage={currentPage}
          finnishLanguage={finnishLanguage}
          backTo={backTo}
        />
      </CSSTransition>
      <CSSTransition
        in={cartDrawerOpen}
        timeout={250}
        classNames={{
          enter: classes.cartdrawerEnter,
          enterActive: classes.cartdrawerEnterActive,
          enterDone: classes.cartdrawerEnterDone,
          exit: classes.cartdrawerExit,
          exitActive: classes.cartdrawerExitActive,
        }}
        unmountOnExit
      >
        {checkout && <CartDrawer setCartDrawerOpen={setCartDrawerOpen} />}
      </CSSTransition>
    </header>
  ) : (
    <header className="header">
      <div className={classes.topTitle}>
        <h1>{topTitle}</h1>
      </div>
      <div className={classes.back}>
        <div className={classes.box}>
          <a href="https://kyrodistillery.com">{backTo}</a>
        </div>
        <div className={classes.box}>
          <Link to={finnishLanguage ? '/fi/' : '/'}>{navigation[0].title}</Link>
        </div>
        <div className={classes.box}>
          <CartButton
            cartDrawerOpen={cartDrawerOpen}
            setCartDrawerOpen={setCartDrawerOpen}
          />
          <div className={classes.dropdown}>
            <button
              className={classes.btn}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {getLocalizedName(locale, locale)}
            </button>
            <div
              className={classes.localeContainer}
              onMouseLeave={() => setDropdownOpen(false)}
              role="button"
              tabIndex="0"
            >
              <div
                className={`${classes.dropBtn} ${classes.locale} ${
                  dropdownOpen ? classes.visible : ''
                }`}
              >
                {Object.keys(translations).map((t) => (
                  <Link key={t} to={translations[t]}>
                    {getLocalizedName(t, locale)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <MenuLayout
        items={navigation}
        currentPage={currentPage}
        finnishLanguage={finnishLanguage}
      />
      <CSSTransition
        in={cartDrawerOpen}
        timeout={250}
        classNames={{
          enter: classes.cartdrawerEnter,
          enterActive: classes.cartdrawerEnterActive,
          enterDone: classes.cartdrawerEnterDone,
          exit: classes.cartdrawerExit,
          exitActive: classes.cartdrawerExitActive,
        }}
        unmountOnExit
      >
        <CartDrawer setCartDrawerOpen={setCartDrawerOpen} language={locale} />
      </CSSTransition>
    </header>
  );
};

Header.propTypes = {
  navigation: PropTypes.array.isRequired,
  currentPage: PropTypes.string,
  translations: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  finnishLanguage: PropTypes.bool,
  backTo: PropTypes.string.isRequired,
  topTitle: PropTypes.string.isRequired
};

export default Header;
